// One Hiring Round: its setup, its candidates and its activity.
//
// H-16c: a closed round is still fully viewable, read-only, with every profile,
// analysis, transcript and activity entry, until each candidate's retention
// period ends. That is not a nicety: a candidate's request for an explanation
// usually arrives after the rejection, which is exactly when a closed round
// would otherwise be gone.

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { hiringApi, hiringMessage } from '../../../../api/hiring';
import { useAdminAccess } from '../../../../hooks/useAdminAccess';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import {
  useHiringAccess,
  useHiringCandidates,
  useHiringOrg,
  useHiringRound,
  useRefreshHiring
} from '../../../../queries/useHiring';
import {
  ActionButton,
  BODY,
  BRANDY_FILL,
  BRANDY_FRAME,
  DANGER,
  LINE,
  Note,
  SECTION_TITLE,
  TEXT
} from '../capability-setup/designSystem';
import { TabButton } from '../Program/components/program tabs/InsightsPage';
import { ButtonSlot, PANEL_INSET, PANEL_SX, ROUND_BUTTON_WIDTHS, RoundStatusChip, day } from './hiringParts';
import RoundSetup from './RoundSetup';
import RoundCandidates from './RoundCandidates';
import RoundActivity from './RoundActivity';
import CandidateAnalysisModal from './CandidateAnalysisModal';
import CompareDialog from './CompareDialog';
import CloseRoundDialog, { ReopenRoundDialog } from './CloseRoundDialog';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

type Tab = 'setup' | 'candidates' | 'activity';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'setup', label: 'Assessment' },
  { key: 'candidates', label: 'Candidates' },
  { key: 'activity', label: 'Activity' }
];
const TAB_DIVIDE = { width: '1px', height: 18, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' };
const DETAIL_DIVIDE = { width: '1px', height: 14, flexShrink: 0, bgcolor: '#3A3A3A' };

const RoundPage: React.FC = () => {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const org = useHiringOrg();
  const refresh = useRefreshHiring();
  const access = useHiringAccess();
  // Only an org admin may hire someone straight in as an admin; the backend
  // refuses it for a team manager, so the option is hidden rather than offered
  // and then rejected (H-26, step 3).
  const { access: adminAccess } = useAdminAccess();
  const { isPlatformAdmin } = useProgramContext();
  const round = useHiringRound(roundId);
  const candidates = useHiringCandidates(roundId);
  const isMobileApp = useIsMobileApp();
  // null until the owner picks a tab.
  const [tab, setTab] = useState<Tab | null>(null);
  const [toolbar, setToolbar] = useState<HTMLElement | null>(null);
  const [exportSlot, setExportSlot] = useState<HTMLElement | null>(null);
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
  const [openCandidate, setOpenCandidate] = useState<string | null>(null);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = useMemo(
    () => (access.data?.roles || []).find((r) => r.id === round.data?.roleId),
    [access.data, round.data]
  );
  const teams = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of access.data?.roles || []) seen.set(r.teamId, r.teamName);
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [access.data]);
  const roles = useMemo(
    () => (access.data?.roles || []).filter((r) => r.ready).map((r) => ({ id: r.id, name: r.name, teamId: r.teamId })),
    [access.data]
  );

  // A round starts on its setup while it is a draft, and on its candidates once
  // it is open: that is what the owner came for at each stage.
  const effectiveTab: Tab = tab ?? (round.data?.status === 'draft' ? 'setup' : 'candidates');

  const close = async (rationale: string) => {
    setBusy(true);
    setError(null);
    try {
      await hiringApi.closeRound(roundId as string, rationale, org);
      refresh({ roundId });
      setClosing(false);
    } catch (err) {
      setError(hiringMessage(err, 'The round could not be closed'));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    setError(null);
    try {
      await hiringApi.reopenRound(roundId as string, org);
      refresh({ roundId });
      setReopening(false);
    } catch (err) {
      setError(hiringMessage(err, 'The round could not be reopened'));
    } finally {
      setBusy(false);
    }
  };

  if (round.isLoading || access.isLoading) {
    return (
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '12px', ...fadeInSx }}>
        <Bone width="40%" height={24} />
        <Bone height={200} />
      </Box>
    );
  }
  if (!round.data) {
    return (
      <Box sx={{ p: '24px' }}>
        <Typography sx={{ ...BODY, color: DANGER }}>This Hiring Round could not be opened.</Typography>
        <Box sx={{ mt: '12px' }}>
          <ActionButton onClick={() => navigate('/app/hiring')}>Back to Hiring</ActionButton>
        </Box>
      </Box>
    );
  }

  const { data } = round;
  const list = candidates.data || [];
  // Anyone already told, in a round that was reopened, isn't emailed a second time.
  const emailCount = list.filter(
    (c) => ['invited', 'started', 'completed'].includes(c.status) && !c.toldUnsuccessful
  ).length;
  const hiredCount = list.filter((c) => c.status === 'hired').length;
  const details = [
    data.roleName,
    data.teamName,
    [data.hiringLocation.country, data.hiringLocation.state].filter(Boolean).join('-'),
    `Data Retention: ${data.retentionMonths} Months`,
    data.closedAt ? `Closed ${day(data.closedAt)}` : ''
  ].filter(Boolean);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: '#0F0F0F',
        px: '25px',
        pt: '20px',
        pb: '16px',
        gap: '12px',
        [MOBILE_SX]: { height: 'auto', overflow: 'visible', px: 0, pt: 0 }
      }}
    >
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        {/* In the brandy-framed fill the learner dashboard's gamification uses. */}
        <Box
          sx={{
            minWidth: 0,
            minHeight: 40,
            boxSizing: 'border-box',
            p: '6px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            bgcolor: BRANDY_FILL,
            border: BRANDY_FRAME,
            borderRadius: '4px'
          }}
        >
          <Typography sx={{ ...SECTION_TITLE, fontSize: 18, whiteSpace: 'normal' }}>{data.name}</Typography>
          <RoundStatusChip status={data.status} />
          {details.map((detail) => (
            <React.Fragment key={detail}>
              <Box sx={DETAIL_DIVIDE} />
              <Typography sx={{ ...BODY, fontSize: 12.5, whiteSpace: 'nowrap' }}>{detail}</Typography>
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: '10px', mr: `${PANEL_INSET}px`, [MOBILE_SX]: { mr: 0 } }}>
          <ButtonSlot width={ROUND_BUTTON_WIDTHS.right}>
            <ActionButton onClick={() => navigate('/app/hiring')}>← Hiring</ActionButton>
          </ButtonSlot>
          {/* Each tab puts its own Export here. */}
          <Box
            ref={setExportSlot}
            sx={{
              width: ROUND_BUTTON_WIDTHS.left,
              flexShrink: 0,
              display: 'flex',
              '& > span, & button': { width: '100%' }
            }}
          />
          <ButtonSlot width={ROUND_BUTTON_WIDTHS.right}>
            {data.status === 'closed' ? (
              <ActionButton primary onClick={() => setReopening(true)}>
                Reopen Round
              </ActionButton>
            ) : (
              <ActionButton
                disabled={data.status !== 'open'}
                reason={data.status === 'draft' ? 'A round can be closed once it is open' : undefined}
                onClick={() => setClosing(true)}
              >
                Close Round
              </ActionButton>
            )}
          </ButtonSlot>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ ...BODY, color: DANGER, flexShrink: 0 }} role="alert">
          {error}
        </Typography>
      )}

      {data.status === 'closed' && data.closeRationale && (
        <Box sx={{ flexShrink: 0, p: '12px 14px', border: `1px solid ${LINE}`, borderRadius: '4px' }}>
          <Typography sx={{ ...BODY, fontSize: 12.5 }}>
            <Box component="span" sx={{ color: TEXT, fontWeight: 600 }}>
              Why this round was closed:
            </Box>{' '}
            {data.closeRationale}
          </Typography>
        </Box>
      )}

      <Box
        role="tablist"
        sx={{
          flexShrink: 0,
          // Tall enough for the Candidates tab's buttons, so it doesn't jump on the tabs without them.
          minHeight: 62,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          p: '10px 14px',
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          borderRadius: '8px',
          [MOBILE_SX]: { gap: '8px', px: '12px', overflowX: 'auto' }
        }}
      >
        {TABS.map((item, i) => (
          <React.Fragment key={item.key}>
            {i > 0 && <Box sx={TAB_DIVIDE} />}
            <TabButton
              label={item.label}
              size={isMobileApp ? 14 : 17}
              active={effectiveTab === item.key}
              onClick={() => setTab(item.key)}
            />
          </React.Fragment>
        ))}
        {/* The Candidates tab puts its buttons here. */}
        <Box
          ref={setToolbar}
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'wrap'
          }}
        />
      </Box>

      {effectiveTab !== 'candidates' && (
        <Box role="tabpanel" sx={PANEL_SX}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', [MOBILE_SX]: { overflow: 'visible' } }}>
            {effectiveTab === 'setup' ? (
              <RoundSetup round={data} role={role} exportSlot={exportSlot} footerSlot={footerSlot} />
            ) : (
              <RoundActivity round={data} candidates={list} exportSlot={exportSlot} />
            )}
          </Box>
        </Box>
      )}
      {effectiveTab === 'setup' && data.status !== 'draft' && (
        <Note>
          The assessment is locked: the round is {data.status}. The levels candidates are graded against were
          snapshotted when it opened, so later edits to the role don&apos;t change how anyone was graded.
        </Note>
      )}
      {/* A draft's Assessment tab puts its Open bar here, always in view under what scrolls. */}
      <Box
        ref={setFooterSlot}
        sx={{
          flexShrink: 0,
          '&:empty': { display: 'none' },
          '& > div': { p: 0, borderTop: 'none', bgcolor: 'transparent' }
        }}
      />
      {effectiveTab === 'candidates' && (
        <RoundCandidates
          round={data}
          onOpenCandidate={setOpenCandidate}
          onCompare={setCompareIds}
          teams={teams}
          roles={roles}
          canHireAsAdmin={isPlatformAdmin || Boolean(adminAccess?.orgAdmin)}
          rationaleTooltip={access.data?.rationaleTooltip ?? null}
          toolbar={toolbar}
          exportSlot={exportSlot}
        />
      )}

      <CandidateAnalysisModal candidateId={openCandidate} round={data} onClose={() => setOpenCandidate(null)} />
      {compareIds.length === 2 && (
        <CompareDialog roundId={data.id} ids={compareIds} attempts={data.attempts} onClose={() => setCompareIds([])} />
      )}
      <CloseRoundDialog
        open={closing}
        round={data}
        emailCount={emailCount}
        hiredCount={hiredCount}
        tooltip={access.data?.rationaleTooltip ?? null}
        busy={busy}
        error={error}
        onClose={close}
        onCancel={() => setClosing(false)}
      />
      <ReopenRoundDialog
        open={reopening}
        round={data}
        busy={busy}
        error={error}
        onReopen={reopen}
        onCancel={() => setReopening(false)}
      />
    </Box>
  );
};

export default RoundPage;
